/*
 * <twc-location-map> — one pin, one place, one Google Maps script.
 *
 * The section (sections/twc-location-map.liquid) has already decided whether a
 * map is possible: it renders NOTHING when the key or the coordinates are
 * missing, so this element never has to think about an empty state. It is
 * handed a key, a latitude, a longitude and a zoom, and it draws them.
 *
 * Loading rules, in order of importance:
 *   1. ONE script tag per document, however many maps a page holds. The loader
 *      promise is module-level and the tag carries `data-twc-maps`, so a
 *      re-render, a second instance or a duplicated asset tag all reuse it.
 *   2. The script is fetched on connect, not at parse time, and the element
 *      does nothing until it is in the document.
 *   3. Every listener rides one AbortController, so disconnectedCallback drops
 *      the lot in one call (theme convention).
 *
 * The palette is read off the events and clubhouses design boards: a
 * desaturated near-white ground, white roads, pale sage water, no points of
 * interest, and ONE gold pin. Google's styles array only applies to a map
 * WITHOUT a cloud `mapId`,
 * which is also why the pin is a classic Marker with an inline SVG icon rather
 * than an AdvancedMarkerElement.
 */
(function (global) {
  'use strict';

  if (global.customElements && global.customElements.get('twc-location-map')) return;

  var MAPS_CALLBACK = '__twcMapsReady';
  var loaderPromise = null;

  /* ---- 1. the map skin -------------------------------------------------- */

  var MAP_STYLE = [
    { elementType: 'geometry', stylers: [{ color: '#f9f9f9' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#4a4a4e' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
    { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#cfdedb' }, { visibility: 'on' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#7b7477' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#ededed' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c4d8d4' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#7b7477' }] },
  ];

  var PIN_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">' +
    '<path fill="#c17f2d" d="M16 0C7.16 0 0 7.16 0 16c0 11 16 24 16 24s16-13 16-24c0-8.84-7.16-16-16-16z"/>' +
    '<circle cx="16" cy="15" r="5" fill="#ffffff"/>' +
    '</svg>';

  /* ---- 2. the single script load ---------------------------------------- */

  function loadMapsApi(apiKey) {
    if (global.google && global.google.maps && global.google.maps.Map) {
      return Promise.resolve();
    }
    if (loaderPromise) return loaderPromise;

    var existing = document.querySelector('script[data-twc-maps]');

    loaderPromise = new Promise(function (resolve, reject) {
      var previous = global[MAPS_CALLBACK];
      global[MAPS_CALLBACK] = function () {
        if (typeof previous === 'function') previous();
        resolve();
      };

      if (existing) {
        // A tag is already in flight (duplicate asset include). Wait for its
        // callback rather than asking Google for a second copy.
        existing.addEventListener('error', function () {
          reject(new Error('twc-map: Google Maps failed to load'));
        });
        return;
      }

      var script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?loading=async&callback=' +
        MAPS_CALLBACK +
        '&key=' +
        encodeURIComponent(apiKey);
      script.async = true;
      script.setAttribute('data-twc-maps', '');
      script.addEventListener('error', function () {
        reject(new Error('twc-map: Google Maps failed to load'));
      });
      document.head.appendChild(script);
    });

    return loaderPromise;
  }

  /* ---- 3. the element --------------------------------------------------- */

  var TwcLocationMap = class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.canvas = this.querySelector('[data-twc-map-canvas]');

      var apiKey = this.getAttribute('data-key');
      var lat = parseFloat(this.getAttribute('data-lat'));
      var lng = parseFloat(this.getAttribute('data-lng'));

      if (!this.canvas || !apiKey || !isFinite(lat) || !isFinite(lng)) return;

      var self = this;
      loadMapsApi(apiKey)
        .then(function () {
          if (!self.isConnected || self.controller.signal.aborted) return;
          self.draw(lat, lng);
        })
        .catch(function () {
          // A dead key or a blocked script must not leave a grey box on the
          // page: the section collapses to nothing, exactly as it does when
          // the key is missing.
          self.setAttribute('hidden', '');
        });
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.map = null;
      this.marker = null;
    }

    draw(lat, lng) {
      var maps = global.google.maps;
      var center = { lat: lat, lng: lng };
      var zoom = parseInt(this.getAttribute('data-zoom'), 10);

      this.map = new maps.Map(this.canvas, {
        center: center,
        zoom: isFinite(zoom) ? zoom : 15,
        styles: MAP_STYLE,
        disableDefaultUI: true,
        fullscreenControl: true,
        gestureHandling: 'cooperative',
        clickableIcons: false,
      });

      this.marker = new maps.Marker({
        map: this.map,
        position: center,
        title: this.getAttribute('data-label') || '',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(PIN_SVG),
          scaledSize: new maps.Size(32, 40),
          anchor: new maps.Point(16, 40),
        },
      });

      // The pin stays centred through every resize — the band is full-bleed and
      // its aspect ratio changes with the viewport.
      var self = this;
      global.addEventListener(
        'resize',
        function () {
          if (self.map) self.map.setCenter(center);
        },
        { signal: this.controller.signal }
      );
    }
  };

  if (global.customElements) {
    global.customElements.define('twc-location-map', TwcLocationMap);
  }
})(window);
